/**
 * The small live-call pieces: the dot, the running clock, the tool strip.
 *
 * THE SESSION-STATE PILL IS NOT HERE. It lives in components/state-pill.tsx with
 * the evidence and recording pills, because the list, the session page and this
 * screen must render a `LIVE_DEGRADED_NO_SIDEBAND` identically — a second pill
 * that drifted a shade would be worse than no pill at all.
 *
 * The clock is its own component with its own interval on purpose: it ticks once
 * a second, and in the page it would re-render the transcript and the whole
 * diagnostics grid sixty times a minute for nothing.
 */
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

export function LiveDot({ label }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="live-dot" aria-hidden />
      <span className="text-xs font-medium uppercase tracking-wide">{label ?? 'live'}</span>
    </span>
  )
}

function mmss(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function Elapsed({ fromMs, toMs }: { fromMs: number | null; toMs?: number | null }) {
  const [now, setNow] = useState(() => Date.now())
  const frozen = toMs != null
  useEffect(() => {
    if (fromMs == null || frozen) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [fromMs, frozen])
  if (fromMs == null) return <span className="mono num faint">--:--</span>
  return <span className="mono num tabular-nums">{mmss((toMs ?? now) - fromMs)}</span>
}

